import React from 'react';
import { cn } from '@cotera/client/v0/lib/utils';
import { Check } from 'lucide-react';

type StepIndicatorProps = {
  currentStep: number;
  totalSteps: number;
  completedSteps: number;
};

/**
 * Visual step indicator showing progress through the hijacker queue.
 * Displays dots for each step with different states:
 * - Completed: filled with checkmark
 * - Active: filled and larger
 * - Pending: outlined/empty
 */
export const HijackerStepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  totalSteps,
  completedSteps,
}) => {
  if (totalSteps <= 1) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: totalSteps }).map((_, idx) => {
        const stepNumber = idx + 1;
        const isCompleted = idx < completedSteps;
        const isActive = stepNumber === currentStep;
        const isPending = idx >= completedSteps + 1;

        return (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <div
                className={cn(
                  'w-3 h-px',
                  isCompleted ? 'bg-primary' : 'bg-muted-foreground/30'
                )}
              />
            )}
            <div
              className={cn(
                'relative flex items-center justify-center rounded-full transition-all duration-200',
                isActive
                  ? 'w-5 h-5 bg-primary text-primary-foreground'
                  : isCompleted
                  ? 'w-4 h-4 bg-primary/80 text-primary-foreground'
                  : 'w-4 h-4 border border-muted-foreground/40 bg-background'
              )}
            >
              {isCompleted && !isActive ? (
                <Check className="w-2.5 h-2.5" />
              ) : (
                <span
                  className={cn(
                    'text-[10px] font-medium',
                    isActive
                      ? 'text-primary-foreground'
                      : isPending
                      ? 'text-muted-foreground'
                      : 'text-primary-foreground'
                  )}
                >
                  {stepNumber}
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

type CompactStepIndicatorProps = {
  currentStep: number;
  totalSteps: number;
};

/**
 * Compact text-based indicator: "Step 2 of 5"
 */
export const CompactStepIndicator: React.FC<CompactStepIndicatorProps> = ({
  currentStep,
  totalSteps,
}) => {
  if (totalSteps <= 1) {
    return null;
  }

  return (
    <span className="text-xs text-muted-foreground whitespace-nowrap">
      Step <span className="font-medium text-foreground">{currentStep}</span> of{' '}
      {totalSteps}
    </span>
  );
};
